import { Injectable, inject } from '@angular/core';
import { APP_CONFIG } from '@app/core/app-config';

/**
 * `window.open` target name V2's legacy Frames launch uses for V4
 * (`PkgSutl01a` → `ShowDocument(url, "LINQ ERP V4")`). MUST match V4's
 * `window.name` (set in V4's index.html) so the browser reuses the existing
 * V4 tab. ERP-15430.
 */
const V4_TAB_NAME = 'LINQ ERP V4';

/**
 * `postMessage` envelope type sent to an already-open V4 tab to launch a page
 * in place (no reload). Mirror of V4→V2's `erp-v2-launch-form`; keep in sync
 * with V4's `LaunchPageListenerService`.
 */
const V4_LAUNCH_MESSAGE_TYPE = 'erp-v4-launch-page';

/** ACK V4 posts back once it has navigated in place. */
const V4_LAUNCH_ACK_TYPE = 'erp-v4-launch-page-ack';

/**
 * How long to wait for V4's ACK before falling back to a full-reload
 * `window.open`. Generous enough for a same-machine postMessage round-trip
 * yet short enough that a genuinely-absent listener (old V4 build) degrades
 * to the previous reload behavior quickly.
 */
const ACK_TIMEOUT_MS = 500;

/**
 * Makes V2→V4 page launches reuse an already-open V4 tab WITHOUT reloading it
 * (ERP-15430) — the mirror of V4's `LaunchV2FormService` + V2's
 * `PendingLaunchService`.
 *
 * V2's launch is server-side (`PkgSutl01a` → Frames `ShowDocument(url,
 * "LINQ ERP V4")`), which the Frames client turns into
 * `window.open(url, "LINQ ERP V4")`. Because the target name matches V4's
 * `window.name`, the browser already REUSES the V4 tab — but it passes a full
 * URL, so it fully re-navigates (reloads) the V4 SPA every time. That reload
 * also wipes `LaunchV2FormService`'s retained V2 window handle, forcing the
 * next V4→V2 launch back to a reload too.
 *
 * This service installs a narrow `window.open` shim: for the `"LINQ ERP V4"`
 * target only, it `postMessage`s the target URL to the existing V4 tab (which
 * navigates its router in place and ACKs) instead of re-navigating. Everything
 * else — including the server-side URL building, `filename_v4_override`
 * gating, and `{token}` substitution in `PkgSutl01a` — is untouched. If no V4
 * tab is reachable, or V4 doesn't ACK in time, it falls back to the original
 * `window.open` so the launch always happens.
 */
@Injectable({ providedIn: 'root' })
export class LaunchV4PageService {
  private readonly appConfig = inject(APP_CONFIG);

  /** Handle to a V4 tab THIS tab opened. Retained so subsequent launches reuse it. */
  private v4Window: Window | null = null;

  /** Original `window.open`, captured on install so the shim can delegate/restore. */
  private originalOpen: typeof window.open | undefined;
  private installed = false;

  /**
   * Install the `window.open` shim. Idempotent, and a no-op when no V4
   * frontend is configured (nothing to launch/intercept).
   */
  install(): void {
    if (this.installed || !this.v4FrontendOrigin()) {
      return;
    }
    this.originalOpen = window.open.bind(window);

    window.open = ((url?: string | URL, target?: string, features?: string): Window | null => {
      // Normalize a URL object to its string form (URL.toString() === href) so
      // the intercept matches and `launchV4` gets a plain string. Delegation
      // below passes the ORIGINAL `url` through unchanged — native window.open
      // accepts `string | URL` and stringifies correctly, so no cast is needed.
      const urlStr = url == null ? url : url.toString();
      if (target === V4_TAB_NAME && typeof urlStr === 'string' && urlStr) {
        return this.launchV4(urlStr, features);
      }
      return this.originalOpen!(url, target, features);
    }) as typeof window.open;

    this.installed = true;
  }

  /** Restore the original `window.open`. Idempotent. */
  uninstall(): void {
    if (this.installed && this.originalOpen) {
      window.open = this.originalOpen;
      this.installed = false;
    }
  }

  /**
   * Reuse an open V4 tab via `postMessage` (no reload) when possible; otherwise
   * open/navigate it the original way. Always returns a window handle (or null)
   * so the Frames caller behaves exactly as before.
   */
  private launchV4(url: string, features?: string): Window | null {
    const v4Origin = this.v4FrontendOrigin();

    let urlOrigin: string | null = null;
    try {
      urlOrigin = new URL(url).origin;
    } catch {
      urlOrigin = null;
    }

    // Only ever reuse for the KNOWN V4 origin. Anything else → original open.
    if (!v4Origin || urlOrigin !== v4Origin) {
      this.v4Window = this.originalOpen!(url, V4_TAB_NAME, features);
      return this.v4Window;
    }

    // Pick a handle to the live V4 tab: one we opened, or our opener (the V4
    // tab that opened THIS V2 tab in the v4→v2 flow). `postMessage` is pinned
    // to `v4Origin`, so it only ever delivers to the real V4 app.
    const opener = window.opener as Window | null;
    const target: Window | null =
      this.v4Window && !this.v4Window.closed
        ? this.v4Window
        : opener && !opener.closed
          ? opener
          : null;

    if (!target) {
      // No reachable V4 tab — open for real and retain the handle.
      this.v4Window = this.originalOpen!(url, V4_TAB_NAME, features);
      return this.v4Window;
    }

    // Optimistically post the launch; if V4 doesn't ACK in time (old build, or
    // the handle wasn't actually the V4 tab) fall back to a full-reload open so
    // the launch still happens.
    const nonce = `${Date.now()}-${Math.random()}`;
    let acked = false;
    const ackHandler = (event: MessageEvent): void => {
      const data = event.data as { type?: string; nonce?: string } | undefined;
      if (event.origin === v4Origin && data?.type === V4_LAUNCH_ACK_TYPE && data.nonce === nonce) {
        acked = true;
        window.removeEventListener('message', ackHandler);
      }
    };
    window.addEventListener('message', ackHandler);

    target.postMessage({ type: V4_LAUNCH_MESSAGE_TYPE, url, nonce }, v4Origin);
    try {
      target.focus();
    } catch {
      // Cross-origin focus may be blocked; harmless.
    }

    window.setTimeout(() => {
      if (!acked) {
        window.removeEventListener('message', ackHandler);
        this.v4Window = this.originalOpen!(url, V4_TAB_NAME, features);
      }
    }, ACK_TIMEOUT_MS);

    return target;
  }

  /** Origin of the configured V4 frontend (`app-config.v4FrontendUrl`), or null. */
  private v4FrontendOrigin(): string | null {
    const url = this.appConfig?.v4FrontendUrl;
    if (!url) {
      return null;
    }
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }
}
